import { Logger } from "../core/Logger.js";

/**
 * The MouseInputOptions class represents configuration options for capturing mouse input.
 */
class MouseInputOptions {
  constructor() {
    this.mousePosition = true;
    this.mouseButtons = true;
    this.mouseWheel = true;
  }
}

/**
 *
 */
class MouseInputInternal {
  constructor() {
    this.x = 0;
    this.y = 0;
    this.pressedButtons = new Set();
    this.releasedButtons = new Set();
    this.currentScrollY = 0;
    this.lastScrollY = 0;
  }

  /**
   *
   */
  startCapture(options) {
    if (typeof window === 'undefined') {
      Logger.warn('Window is not defined. Event listeners cannot be registered.');
      return;
    }

    if (options.mousePosition == true) {
      this.mousemoveHandler = event => {
        this.x = event.clientX;
        this.y = event.clientY;
      };
      window.addEventListener('mousemove', this.mousemoveHandler);
    }

    if (options.mouseButtons == true) {
      this.mousedownHandler = event => {
        this.pressedButtons.add(event.button);
        this.releasedButtons.delete(event.button);
      };

      this.mouseupHandler = event => {
        this.pressedButtons.delete(event.button);
        this.releasedButtons.add(event.button);
      };

      window.addEventListener('mousedown', this.mousedownHandler);
      window.addEventListener('mouseup', this.mouseupHandler);
    }

    if (options.mouseWheel == true) {
      this.wheelHandler = event => {
        this.currentScrollY += event.deltaY;
      };
      window.addEventListener('wheel', this.wheelHandler);
    }
  }

  /**
   *
   */
  stopCapture() {
    this.pressedButtons.clear();
    this.releasedButtons.clear();
    window.removeEventListener('mousemove', this.mousemoveHandler);
    window.removeEventListener('mousedown', this.mousedownHandler);
    window.removeEventListener('mouseup', this.mouseupHandler);
    window.removeEventListener('wheel', this.wheelHandler);
  }

  isButtonPressed(button) {
    return this.pressedButtons.has(button);
  }

  isButtonReleased(button) {
    if (this.releasedButtons.has(button)) {
      this.releasedButtons.delete(button);
      return true;
    }
    return false;
  }

  getPosition() {
    return { x: this.x, y: this.y };
  }

  getScrollDelta() {
    const deltaY = this.lastScrollY - this.currentScrollY;
    this.lastScrollY = this.currentScrollY;
    return deltaY;
  }
}

/**
 * The DefaultMouseInputOptions constant is an instance of the MouseInputOptions class
 * that provides default settings for capturing mouse input
 */
const DefaultMouseInputOptions = new MouseInputOptions();

/**
 * The MouseButton enum provides constants representing the different mouse buttons.
 */
export const MouseButton = {
  /**
   * Represents the left mouse button.
   */
  LEFT: 0,
  /**
   * Represents the middle mouse button (often associated with the scroll wheel button).
   */
  MIDDLE: 1,
  /**
   * Represents the right mouse button.
   */
  RIGHT: 2,
  /**
   * Represents an unknown or unspecified mouse button.
   */
  UNKNOWN: -1,
};

/**
 * The MouseInput class is designed to help you handle mouse input in your applications.
 */
export class MouseInput {
  static _mouseInput = new MouseInputInternal();

  /**
   * Starts capturing mouse input based on the provided options.
   * @param {*} options Configuration for capturing mouse input.
   */
  static startCapture(options = DefaultMouseInputOptions) {
    MouseInput._mouseInput.startCapture(options);
  }

  /**
   * Stops capturing mouse input by removing all event listeners.
   */
  static stopCapture() {
    MouseInput._mouseInput.stopCapture();
  }

  /**
   * Checks if a specific mouse button is currently pressed.
   * @param {*} button The mouse button to check (e.g., MouseButton.LEFT).
   * @returns true if the button is pressed, false otherwise.
   */
  static isButtonPressed(button) {
    return MouseInput._mouseInput.isButtonPressed(button);
  }

  /**
   * Checks if a specific mouse button has been released.
   */
  static isButtonReleased(button) {
    return MouseInput._mouseInput.isButtonReleased(button);
  }

  /**
   * Gets the current mouse position in screen space.
   * @returns An object with x and y properties representing the horizontal and vertical position of the mouse pointer.
   */
  static getPosition() {
    return MouseInput._mouseInput.getPosition();
  }

  /**
   * Gets the change in scroll values (delta) since the last scroll event.
   * Positive values indicate scrolling upward.
   * Negative values indicate scrolling downward.
   */
  static getScrollDelta() {
    return MouseInput._mouseInput.getScrollDelta();
  }
}
